import { ZoomToVis, type ZoomToVisTransformation } from "@/animations/ZoomToVis";
import { canv } from "@/canv/global";
import { getViewport } from "@/canvasController/helpers";
import { eventEmitter } from "@/eventEmitter";
import { generateColor } from "@/helpers/generateColor";
import type { BoardUserData, FastSelectMessageData, FastViewMessageData } from "@/intercom/types";
import { MultiUser } from "./index";

type MultiUserSyncData = {
    sessionUID: string;
    name?: string;
    token?: string;
    clientUID?: string;
    accountID?: number;
};

type EnsureMultiUserResult = {
    multiUser: MultiUser | null;
    isNew: boolean;
};

export class multiManager {
    multiUsersMap: Map<string, MultiUser> = new Map();
    viewSubscribeUID: string = "";
    disableMultiCursors: boolean = false;

    private pendingViewDataMap: Map<string, FastViewMessageData> = new Map();
    private pendingSelectDataMap: Map<string, string[]> = new Map();
    private lastSentPosition: any = null;
    private rafId: number | null = null;
    private hasPendingUpdate: boolean = false;
    private lastSentTime: number = 0;
    private lastSentViewport: any = null;
    private minIntervalMs: number = 200;
    autoFollowWasApplied: boolean = false;

    constructor() {
        eventEmitter.dispatch("multi.cursors", {
            disableMultiCursors: this.disableMultiCursors,
        });
        this.init();
    }

    init() {
        this.scheduleRAF();
    }

    enableCursorUpdate = () => {
        this.hasPendingUpdate = true;
    };

    private hasChanged(pos: { x: number; y: number }, viewport: any): boolean {
        const posChanged =
            !this.lastSentPosition ||
            this.lastSentPosition.x !== pos.x ||
            this.lastSentPosition.y !== pos.y;

        const viewportChanged =
            !this.lastSentViewport ||
            JSON.stringify(this.lastSentViewport) !== JSON.stringify(viewport);

        return posChanged || viewportChanged;
    }

    private scheduleRAF() {
        this.rafId = requestAnimationFrame((timestamp) => {
            if (this.hasPendingUpdate && timestamp - this.lastSentTime >= this.minIntervalMs) {
                const currentPos = canv.multiManager.getCursorPosition();
                const currentViewport = getViewport();

                if (this.hasChanged(currentPos, currentViewport)) {
                    canv.intercom.viewMessage(currentPos, currentViewport);
                    this.lastSentPosition = currentPos;
                    this.lastSentTime = timestamp;
                    this.lastSentViewport = currentViewport;
                }

                this.hasPendingUpdate = false;
            }

            this.scheduleRAF();
        });
    }

    destroy() {
        if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    }

    private isCurrentUserSession(sessionUID: string, clientUID?: string): boolean {
        const currentSessionUID = canv.sessionUID;

        if (sessionUID && currentSessionUID && sessionUID === currentSessionUID) {
            return true;
        }

        const currentClientUID = canv.getInitDataProp("clientUID");

        return Boolean(clientUID && currentClientUID && clientUID === currentClientUID);
    }

    private resolveUserColor(data: MultiUserSyncData): string {
        return generateColor(data.token || data.clientUID || data.sessionUID);
    }

    private createMultiUser(data: MultiUserSyncData): MultiUser {
        return new MultiUser(
            data.name ?? "",
            data.sessionUID,
            data.token,
            this.resolveUserColor(data),
        );
    }

    private applyPendingSelectData(multiUser: MultiUser): boolean {
        const pendingSelectData = this.pendingSelectDataMap.get(multiUser.sessionUID);
        if (!pendingSelectData) {
            return false;
        }

        multiUser.setSelectedGroups(pendingSelectData);
        this.pendingSelectDataMap.delete(multiUser.sessionUID);

        return true;
    }

    private ensureMultiUser(data: MultiUserSyncData): EnsureMultiUserResult {
        const sessionUID = data.sessionUID;

        if (!sessionUID || this.isCurrentUserSession(sessionUID, data.clientUID)) {
            return {
                multiUser: null,
                isNew: false,
            };
        }

        let multiUser = this.findMultiUser(sessionUID);
        const isNew = !multiUser;

        if (!multiUser) {
            multiUser = this.createMultiUser(data);
            this.multiUsersMap.set(sessionUID, multiUser);
        }

        multiUser.updateIdentityData({
            name: data.name,
            token: data.token,
            clientUID: data.clientUID,
            accountID: data.accountID,
        });

        return {
            multiUser,
            isNew,
        };
    }

    private removeMultiUser(sessionUID: string): boolean {
        if (!sessionUID || !this.multiUsersMap.has(sessionUID)) {
            this.pendingViewDataMap.delete(sessionUID);
            this.pendingSelectDataMap.delete(sessionUID);
            return false;
        }

        this.multiUsersMap.delete(sessionUID);
        this.pendingViewDataMap.delete(sessionUID);
        this.pendingSelectDataMap.delete(sessionUID);

        if (this.viewSubscribeUID === sessionUID) {
            this.resetViewSubscribe();
        }

        return true;
    }

    resetMultiUsers() {
        this.multiUsersMap.clear();
        this.pendingViewDataMap.clear();
        this.pendingSelectDataMap.clear();
        this.destroy();
        this.notifyUsersChanged();
        canv.appInterface.usersWindow?.close();
        this.resetViewSubscribe();
        canv.redraw();
        canv.redraw(true);
    }

    private notifyUsersChanged() {
        eventEmitter.dispatch("multi.users.sessions", {
            users: this.getListedUsers(),
        });
        canv.appInterface.usersWindow?.reFillUsers();
    }

    findMultiUser(sessionUID: string): MultiUser | null {
        if (!sessionUID) {
            return null;
        }

        return this.multiUsersMap.get(sessionUID) ?? null;
    }

    getListedUsers(): MultiUser[] {
        return Array.from(this.multiUsersMap.values());
    }

    hasListedUsers(): boolean {
        return this.multiUsersMap.size > 0;
    }

    prioritizeMultiUser(sessionUID: string): void {
        if (!sessionUID) {
            return;
        }

        const prioritizedUser = this.findMultiUser(sessionUID);
        if (!prioritizedUser) {
            return;
        }

        const sortedMultiUsers = new Map<string, MultiUser>([[sessionUID, prioritizedUser]]);

        for (const [currentSessionUID, multiUser] of this.multiUsersMap.entries()) {
            if (currentSessionUID === sessionUID) {
                continue;
            }

            sortedMultiUsers.set(currentSessionUID, multiUser);
        }

        this.multiUsersMap = sortedMultiUsers;
        this.notifyUsersChanged();
    }

    private applyViewDataToMultiUser(multiUser: MultiUser, data: FastViewMessageData): void {
        multiUser.updateVirtualCursor(data.cursorPosition.x, data.cursorPosition.y);
        multiUser.updateViewData(data.viewPosition);

        const currentUserToken = canv.getInitDataProp("userToken");
        const followTargetToken = canv.getInitDataProp("followTargetToken");
        const isCurrentTokenUnequalToTargetToken = followTargetToken !== currentUserToken;
        const isUserTokenEqualToTargetToken = data.token === followTargetToken;
        const isSubscribeUIDUnequalToUserUID = this.viewSubscribeUID !== data.sessionUID;

        if (
            followTargetToken &&
            !this.autoFollowWasApplied &&
            isCurrentTokenUnequalToTargetToken &&
            isUserTokenEqualToTargetToken &&
            isSubscribeUIDUnequalToUserUID
        ) {
            this.viewSubscribeUID = data.sessionUID;
            this.autoFollowWasApplied = true;
            eventEmitter.dispatch("multi.users.trackingSession", { session: data.sessionUID });
        }

        this.applyViewSubscribe();
    }

    private applyPendingViewData(multiUser: MultiUser): boolean {
        const pendingViewData = this.pendingViewDataMap.get(multiUser.sessionUID);
        if (!pendingViewData) {
            return false;
        }

        this.pendingViewDataMap.delete(multiUser.sessionUID);
        this.applyViewDataToMultiUser(multiUser, pendingViewData);

        return true;
    }

    applyBoardUsers(users: BoardUserData[]) {
        const nextBoardUsersSessionUIDs = new Set<string>();
        let shouldRedraw = false;

        for (const user of users) {
            if (
                !user.primarySessionUID ||
                this.isCurrentUserSession(user.primarySessionUID, user.clientUID)
            ) {
                continue;
            }

            nextBoardUsersSessionUIDs.add(user.primarySessionUID);

            const { multiUser } = this.ensureMultiUser({
                sessionUID: user.primarySessionUID,
                name: user.name,
                clientUID: user.clientUID,
                accountID: user.accountID,
            });

            if (!multiUser) {
                continue;
            }

            const appliedPendingSelect = this.applyPendingSelectData(multiUser);
            const appliedPendingView = this.applyPendingViewData(multiUser);

            shouldRedraw = shouldRedraw || appliedPendingSelect || appliedPendingView;
        }

        const sessionUIDsToDelete = Array.from(this.multiUsersMap.keys()).filter(
            (sessionUID) => !nextBoardUsersSessionUIDs.has(sessionUID),
        );

        for (const sessionUID of Array.from(this.pendingViewDataMap.keys())) {
            if (!nextBoardUsersSessionUIDs.has(sessionUID)) {
                this.pendingViewDataMap.delete(sessionUID);
            }
        }

        for (const sessionUID of Array.from(this.pendingSelectDataMap.keys())) {
            if (!nextBoardUsersSessionUIDs.has(sessionUID)) {
                this.pendingSelectDataMap.delete(sessionUID);
            }
        }

        for (const sessionUID of sessionUIDsToDelete) {
            this.removeMultiUser(sessionUID);
        }

        this.notifyUsersChanged();

        if (shouldRedraw || sessionUIDsToDelete.length > 0) {
            canv.redraw();
        }
    }

    applySelectData(data: FastSelectMessageData) {
        if (this.isCurrentUserSession(data.sessionUID)) {
            return;
        }

        const multiUser = this.findMultiUser(data.sessionUID);
        if (!multiUser) {
            this.pendingSelectDataMap.set(data.sessionUID, data.selectedGroups);
            return;
        }

        multiUser.setSelectedGroups(data.selectedGroups);
        canv.redraw();
    }

    applyViewData(data: FastViewMessageData) {
        if (this.isCurrentUserSession(data.sessionUID)) {
            return;
        }

        const multiUser = this.findMultiUser(data.sessionUID);
        if (!multiUser) {
            this.pendingViewDataMap.set(data.sessionUID, data);
            return;
        }

        this.applyViewDataToMultiUser(multiUser, data);
    }

    changeVisibilityCursor(isVisible) {
        for (const multiUser of this.multiUsersMap.values()) {
            multiUser.changeVisibility(isVisible);
        }
    }

    applyViewSubscribe() {
        if (!this.viewSubscribeUID) {
            return;
        }

        const multiUser = this.findMultiUser(this.viewSubscribeUID);

        if (!multiUser || !multiUser.hasViewData()) {
            return;
        }

        const screenData = {
            simulatedSelectingStartX: -canv.m[4] / canv.m[0],
            simulatedSelectingStartY: -canv.m[5] / canv.m[3],
            simulatedSelectingWidth: canv.canvas.width / canv.m[0],
            simulatedSelectingHeight: canv.canvas.height / canv.m[3],
        };

        const selectedProps = multiUser.viewData;

        // Центр вьюпорта другого пользователя
        const targetCenterX = selectedProps["viewportStartX"] + selectedProps["viewportWidth"] / 2;
        const targetCenterY = selectedProps["viewportStartY"] + selectedProps["viewportHeight"] / 2;

        // выбираем меньший коэффициент, чтобы видеть все что видит другой пользователь
        // если выбрать бОльший - будем видеть в том же масштабе, что и другой, но часть контента может обрезаться
        const ratio = Math.min(
            screenData.simulatedSelectingWidth / selectedProps["viewportWidth"],
            screenData.simulatedSelectingHeight / selectedProps["viewportHeight"],
        );

        const goTo: ZoomToVisTransformation = {
            0: canv.m[0] * ratio,
            3: canv.m[3] * ratio,
            4:
                -(targetCenterX - screenData.simulatedSelectingWidth / (2 * ratio)) *
                (canv.m[0] * ratio),
            5:
                -(targetCenterY - screenData.simulatedSelectingHeight / (2 * ratio)) *
                (canv.m[3] * ratio),
        };

        const animation = new ZoomToVis(goTo);
        canv.animator.deleteAnimation("zoomTo");
        canv.animator.addAnimation(animation);
        canv.redraw();
    }

    tryViewSubscribe(sessionUID) {
        const shouldFocus = this.viewSubscribeUID !== sessionUID;
        this.resetViewSubscribe();

        if (!shouldFocus) {
            return;
        }

        const selectedUserLine = document.querySelector(`[sessionUID="${sessionUID}"]`);
        if (selectedUserLine) {
            selectedUserLine.classList.add("usersWindowItemSelectorOn");
        }

        eventEmitter.dispatch("multi.users.trackingSession", {
            session: sessionUID,
        });

        this.viewSubscribeUID = sessionUID;
        this.applyViewSubscribe();
    }

    resetViewSubscribe() {
        this.viewSubscribeUID = "";
        eventEmitter.dispatch("multi.users.trackingSession", { session: null });

        const selectedUserLine = document.querySelector(".usersWindowItemSelectorOn");
        if (selectedUserLine) {
            selectedUserLine.classList.remove("usersWindowItemSelectorOn");
        }

        canv.animator.deleteAnimation("zoomTo");
    }

    getCursorPosition() {
        const pt = canv.actionManager.getActionPoint();

        return { x: pt.x, y: pt.y };
    }

    draw() {
        this.drawView();

        if (this.disableMultiCursors || canv.highloadMode) return;
        for (const multiUser of this.multiUsersMap.values()) {
            multiUser.draw();
        }
    }

    drawView() {
        if (!this.viewSubscribeUID) return;

        const multiUser = this.findMultiUser(this.viewSubscribeUID);
        if (!multiUser || !multiUser.hasViewData()) return;

        const screenData = this.calculateScreenData();

        canv.ctx.strokeStyle = multiUser.color || "red";
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(12);
        canv.ctx.strokeRect(
            screenData.simulatedSelectingStartX,
            screenData.simulatedSelectingStartY,
            screenData.simulatedSelectingWidth,
            screenData.simulatedSelectingHeight,
        );
    }

    private calculateScreenData() {
        return {
            simulatedSelectingStartX: -canv.m[4] / canv.m[0],
            simulatedSelectingStartY: -canv.m[5] / canv.m[3],
            simulatedSelectingWidth: canv.canvas.width / canv.m[0],
            simulatedSelectingHeight: canv.canvas.height / canv.m[3],
        };
    }

    setDisableMultiCursors(value) {
        if (this.disableMultiCursors === value) {
            return;
        }
        this.disableMultiCursors = value;

        eventEmitter.dispatch("multi.cursors", {
            disableMultiCursors: value,
        });

        canv.redraw();
    }
}
